import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import UpgradePrompt from './UpgradePrompt';
import { registerUpgradeHandler } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/**
 * Hard-limit triggers that must ALWAYS re-open the modal when the user hits
 * them again. No 24-hour dismiss, no session dismiss — every blocked action
 * shows the modal anew.
 */
const HARD_LIMIT_TRIGGERS = new Set([
  'patrimony_slot_limit',
  'patrimony_asset_expansion_limit',
  'asset_creation_locked',
  'objective_limit',
  'ideal_template_limit',
  'ideal_template_edit_locked',
  'active_template_locked',
  'budget_period_limit',
  'budget_period_locked_past',
  'budget_period_locked_future',
  'legacy_period_locked',
  'reopen_period_locked',
  'another_period_already_reopened',
  'period_in_standby',
  'budget_period_quota_exceeded',
  'document_storage_full',
  'family_readonly',
  'simulator_pdf_save',
  'partner_invite',
  'fictitious_member',
  'household_max_adults_reached',
]);

// Only soft/proactive triggers may be dismissed for the session
const SESSION_DISMISS_KEY = (triggerId) => `upgrade_session_dismissed_${triggerId}`;

const UpgradePromptContext = createContext(null);

export function UpgradePromptProvider({ children }) {
  const [prompt, setPrompt] = useState(null); // { triggerId, userContext }
  const { user } = useAuth();
  const betaMode = !!user?.beta_mode;

  const showUpgrade = useCallback((triggerId, userContext = {}) => {
    // BETA_MODE: every upgrade prompt is suppressed. Backend gates that
    // would normally trigger them (asset_creation_locked, family_readonly,
    // simulator_pdf_save, …) all pass through in beta because
    // effective_plan resolves to "famille". This is a defensive last
    // line: even if some legacy code path tries to surface a prompt,
    // we just don't render it. Flipping BETA_MODE off restores the
    // normal modal behaviour with zero changes here.
    if (betaMode) {
      // eslint-disable-next-line no-console
      console.info(`[UpgradePrompt] suppressed in BETA_MODE (trigger=${triggerId})`);
      return;
    }
    // Hard limits always show, no matter what
    if (HARD_LIMIT_TRIGGERS.has(triggerId)) {
      setPrompt({ triggerId, userContext });
      return;
    }

    // Proactive triggers may be dismissed for the current session only
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY(triggerId)) === 'true') return;
    } catch {}

    setPrompt({ triggerId, userContext });
  }, [betaMode]);

  const handleDismiss = useCallback(() => {
    const current = prompt?.triggerId;
    setPrompt(null);

    // Only proactive (non-hard-limit) triggers remember the dismissal — per session
    if (current && !HARD_LIMIT_TRIGGERS.has(current)) {
      try { sessionStorage.setItem(SESSION_DISMISS_KEY(current), 'true'); } catch {}
    }
  }, [prompt]);

  // Bridge React context → axios interceptor
  useEffect(() => {
    registerUpgradeHandler(showUpgrade);
    return () => registerUpgradeHandler(null);
  }, [showUpgrade]);

  return (
    <UpgradePromptContext.Provider value={{ showUpgrade }}>
      {children}
      {prompt && (
        <UpgradePrompt
          triggerId={prompt.triggerId}
          userContext={prompt.userContext}
          variant="modal"
          onDismiss={handleDismiss}
        />
      )}
    </UpgradePromptContext.Provider>
  );
}

export function useUpgradePrompt() {
  const ctx = useContext(UpgradePromptContext);
  if (!ctx) {
    throw new Error('useUpgradePrompt must be used within <UpgradePromptProvider>');
  }
  return ctx;
}
